import { useEffect, useState } from 'react';
import { ITypedBaseResponse } from '@nkr/models/base-response';
import { IOpoCase, IOpoCaseCenter, IOpoCaseRequest, IOpoRecipientCenterPoc } from '@nkr/models/opo-case';
import { AppActionCreators } from '@nkr/store/application';
import { AuthActionCreators } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';
import usePortalOpo from './use-portal-opo';

export interface IOpoCaseSaveResult {
    success: boolean,
    message: string
}

interface IRetVal {
    cases: IOpoCase[],
    centers: IOpoCaseCenter[],
    recipientCenterPocs: IOpoRecipientCenterPoc[],
    loading: boolean,
    error: string,
    save: (id: number | null, request: IOpoCaseRequest) => Promise<IOpoCaseSaveResult>,
    remove: (id: number) => Promise<boolean>,
    isCaseNumberAvailable: (caseNumber: string, caseId: number | null) => Promise<boolean>
}

const useOpoCases = (isPast: boolean): IRetVal => {
    const { dispatch } = useAppHookContext();
    const { opoId } = usePortalOpo();
    const [cases, setCases] = useState<IOpoCase[]>([]);
    const [centers, setCenters] = useState<IOpoCaseCenter[]>([]);
    const [recipientCenterPocs, setRecipientCenterPocs] = useState<IOpoRecipientCenterPoc[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [refreshKey, setRefreshKey] = useState<number>(0);

    const baseUrl = `${__API__}/opo/${opoId}/cases`;

    useEffect(() => {
        if (!opoId) {
            return;
        }

        dispatch(AuthActionCreators.apiGet({ url: `${baseUrl}/centers` }))
            .then((response: ITypedBaseResponse<IOpoCaseCenter[]>) => {
                if (response && response.data.success) {
                    setCenters(response.data.results);
                }
            })
            .catch(() => setCenters([]));
    }, [opoId]);

    useEffect(() => {
        if (!opoId) {
            return;
        }

        dispatch(AuthActionCreators.apiGet({ url: `${baseUrl}/recipient-center-pocs` }))
            .then((response: ITypedBaseResponse<IOpoRecipientCenterPoc[]>) => {
                if (response && response.data.success) {
                    setRecipientCenterPocs(response.data.results);
                }
            })
            .catch(() => setRecipientCenterPocs([]));
    }, [opoId, refreshKey]);

    useEffect(() => {
        if (!opoId) {
            return;
        }

        let isCurrent = true;

        const fetchData = async () => {
            setLoading(true);
            setError('');

            await dispatch(AppActionCreators.showLoadingAsync(isPast ? 'Getting past cases, one moment please...' : 'Getting pending cases, one moment please...'));

            try {
                const response: ITypedBaseResponse<IOpoCase[]> = await dispatch(AuthActionCreators.apiGet({ url: `${baseUrl}?past=${isPast}` }));

                if (!isCurrent) {
                    return;
                }

                if (response && response.data.success) {
                    setCases(response.data.results);
                } else {
                    setCases([]);
                    setError('Unable to load cases');
                }
            } catch (err) {
                if (isCurrent) {
                    setCases([]);
                    setError('Unable to load cases');
                }
            } finally {
                if (isCurrent) {
                    setLoading(false);
                    dispatch(AppActionCreators.hideLoading());
                }
            }
        };

        fetchData();

        return () => {
            isCurrent = false;
            dispatch(AppActionCreators.hideLoading());
        };
    }, [opoId, isPast, refreshKey]);

    const save = async (id: number | null, request: IOpoCaseRequest): Promise<IOpoCaseSaveResult> => {
        await dispatch(AppActionCreators.showLoadingAsync('Saving case, one moment please...'));

        try {
            const config = { url: id ? `${baseUrl}/${id}` : baseUrl, data: request };
            const response: ITypedBaseResponse<IOpoCase> = await dispatch(id ? AuthActionCreators.apiPut(config) : AuthActionCreators.apiPost(config));

            if (response && response.data.success) {
                setRefreshKey(x => x + 1);
                return { success: true, message: '' };
            }

            return { success: false, message: (response && response.data.message) || 'Unable to save case' };
        } catch (err) {
            return { success: false, message: 'Unable to save case' };
        } finally {
            dispatch(AppActionCreators.hideLoading());
        }
    };

    const remove = async (id: number): Promise<boolean> => {
        await dispatch(AppActionCreators.showLoadingAsync('Deleting case, one moment please...'));

        try {
            const response: ITypedBaseResponse<null> = await dispatch(AuthActionCreators.apiDelete({ url: `${baseUrl}/${id}` }));

            if (response && response.data.success) {
                setRefreshKey(x => x + 1);
                return true;
            }

            return false;
        } catch (err) {
            return false;
        } finally {
            dispatch(AppActionCreators.hideLoading());
        }
    };

    const isCaseNumberAvailable = async (caseNumber: string, caseId: number | null): Promise<boolean> => {
        const params = new URLSearchParams({ caseNumber });

        if (caseId) {
            params.append('caseId', String(caseId));
        }

        const response: ITypedBaseResponse<boolean> = await dispatch(AuthActionCreators.apiGet({ url: `${baseUrl}/case-number-available?${params}` }));

        return !response || !response.data.success || response.data.results;
    };

    return { cases, centers, recipientCenterPocs, loading, error, save, remove, isCaseNumberAvailable };
}

export default useOpoCases;
